import { isVideoStation } from '@app/core/stations/helpers';
import { useStations } from '@app/core/stations/hooks/useStations';
import {
  StatusFilterType,
  StationTypeFilterType,
} from '@app/modules/logger/screens/status/components/options';
import { STATUS_REFETCH_INTERVAL_MS } from '@app/modules/logger/screens/status/constants';
import { StationStatusListItem } from '@app/modules/logger/screens/status/types';
import { segments } from '@mi-front/api-clients';
import { useMemo } from 'react';
import { useStatusQueryFilter } from './useStatusQueryFilter';

/**
 * Hook that manages all filtering logic for the status screen.
 * URL params are the source of truth for filter state.
 * Returns filter state, handlers, combined statuses (for counts), and filtered statuses (for display).
 */
export function useAllStationsStatuses() {
  const stations = useStations();
  const statusesQuery = segments.useStationStatusGetStatuses(undefined, {
    query: {
      refetchInterval: STATUS_REFETCH_INTERVAL_MS, // Refetch every 15 seconds
    },
  });

  const { queryParams } = useStatusQueryFilter();

  const stationIds = stations.map((station) => station.id);
  const statuses = (statusesQuery.data || []).filter((status) =>
    stationIds.includes(status.stationId),
  );

  const filteredStatuses = useMemo<StationStatusListItem[]>(() => {
    return statuses.map((status) => {
      const station = stations.find((station) => station.id === status.stationId)!;

      const matchesText = (() => {
        const trimmedText = queryParams.text.trim();
        if (!trimmedText) return true;

        // Escape special regex characters and create case-insensitive regex
        const escapedText = trimmedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedText, 'i');

        return regex.test(station.name) || regex.test(station.slug);
      })();

      const matchesActivity =
        queryParams.status === StatusFilterType.ALL ||
        (status.status === 'offline' && queryParams.status === StatusFilterType.OFFLINE) ||
        (status.status === 'online' && queryParams.status === StatusFilterType.ONLINE);

      // No type param (null) = show all. type=tv => only TV, type=radio => only Radio.
      const matchesStationType =
        queryParams.type == null ||
        (queryParams.type === StationTypeFilterType.TV && isVideoStation(station)) ||
        (queryParams.type === StationTypeFilterType.RADIO && !isVideoStation(station));

      const isVisible = matchesText && matchesActivity && matchesStationType;

      return {
        station,
        status,
        isVisible,
      } as StationStatusListItem;
    });
  }, [statuses, stations, queryParams.text, queryParams.status, queryParams.type]);

  return {
    isLoading: statusesQuery.isLoading,
    statuses,
    filteredStatuses,
  };
}
